import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Zap, CheckCircle2, Clock, Store, Sparkles, Rocket } from "lucide-react";
import MetricCard from "@/components/MetricCard";
import { Credits } from "@/components/Currency";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function ResellerUpgradeCard() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reseller/upgrade-info").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);

  if (!info || !info.enabled || info.is_reseller) return null;

  const { threshold, current_credits, eligible } = info;
  const pct = Math.min(100, Math.round((current_credits / threshold) * 100));

  const unlock = async () => {
    setBusy(true);
    try {
      await api.post("/reseller/become");
      await refreshUser();
      toast.success("Revenda desbloqueada! Bem-vindo(a), revendedor(a) 🦊");
      navigate("/revenda");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="reseller-upgrade-card"
      className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-background p-5 sm:p-6"
    >
      <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-amber-500/20 blur-2xl" />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/20 text-amber-500">
            <Store className="h-6 w-6" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-heading text-lg font-bold">Vire Revendedor 🦊</h3>
              {eligible && (
                <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-xs font-semibold text-green-500">Liberado!</span>
              )}
            </div>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              {eligible ? (
                <>Você já tem <b>{threshold} créditos</b>! Crie sua loja, monte sua rede e revenda ativações. <b>Seus créditos continuam seus</b> pra usar normalmente.</>
              ) : (
                <>Faça sua primeira recarga de <b>{threshold} créditos</b> (ou acumule {threshold}) e sua loja fica <b>desbloqueada pra sempre</b>. Sem custo extra — os créditos continuam seus.</>
              )}
            </p>
          </div>
        </div>
        <div className="shrink-0">
          {eligible ? (
            <Button onClick={unlock} disabled={busy} data-testid="reseller-unlock-btn" className="w-full gap-2 bg-amber-500 text-black hover:bg-amber-400 sm:w-auto">
              <Rocket className="h-4 w-4" /> {busy ? "Desbloqueando..." : "Criar minha loja"}
            </Button>
          ) : (
            <Button onClick={() => navigate("/painel/creditos")} data-testid="reseller-recharge-btn" variant="outline" className="w-full gap-2 border-amber-500/40 sm:w-auto">
              <Sparkles className="h-4 w-4" /> Comprar créditos
            </Button>
          )}
        </div>
      </div>
      {!eligible && (
        <div className="relative mt-4">
          <div className="mb-1 flex justify-between text-xs text-muted-foreground">
            <span>{current_credits} / {threshold} créditos</span>
            <span>{pct}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function UserOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [acts, setActs] = useState([]);

  useEffect(() => {
    api.get("/activations/mine?limit=5").then(({ data }) => setActs(data.items)).catch(() => {});
  }, []);

  const approved = acts.filter((a) => a.status === "approved").length;
  const pending = acts.filter((a) => a.status === "pending").length;

  return (
    <div className="space-y-6" data-testid="user-overview">
      <div>
        <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Olá, {user?.name?.split(" ")[0]}</h2>
        <p className="text-sm text-muted-foreground">Bem-vindo ao seu painel VULPIX.</p>
      </div>

      <ResellerUpgradeCard />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard icon={Wallet} label="Meus créditos" value={<Credits value={user?.credits ?? 0} testid="metric-credits-badge" />} testid="metric-credits" />
        <MetricCard icon={Zap} label="Ativações (recentes)" value={acts.length} testid="metric-activations" />
        <MetricCard icon={CheckCircle2} label="Aprovadas" value={approved} testid="metric-approved" />
        <MetricCard icon={Clock} label="Pendentes" value={pending} testid="metric-pending" />
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={() => navigate("/")} className="gap-2" data-testid="overview-activate-btn"><Zap className="h-4 w-4" /> Ativar aplicativo</Button>
        <Button variant="outline" onClick={() => navigate("/painel/creditos")} data-testid="overview-credits-btn">Comprar créditos</Button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border p-4">
          <h3 className="font-heading font-semibold">Ativações recentes</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate("/painel/ativacoes")} data-testid="overview-see-all">Ver todas</Button>
        </div>
        <div className="divide-y divide-border">
          {acts.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Nenhuma ativação ainda.</p>}
          {acts.map((a) => (
            <div key={a.id} className="flex items-center justify-between p-4" data-testid={`recent-act-${a.id}`}>
              <div>
                <p className="text-sm font-medium">{a.app_name}</p>
                <p className="text-xs text-muted-foreground">{a.mac_app}</p>
              </div>
              <StatusBadge status={a.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
